import type { RoutineResp } from "@/types/routines.ts";

/**
 * Which editors the routine detail view should offer for a given routine.
 *
 * Each system routine that needs configuration brings its own dedicated editor
 * rather than a generic settings row, so the Settings tab's contents — and
 * therefore whether it should exist at all — depend on the routine's identity.
 */
export type RoutineCapabilities = {
  /** Edits its label catalog through `LabelCatalogEditor`. */
  isLabelRoutine: boolean;
  /** Chooses which labels to summarize, through `DigestLabelSelector`. */
  isDigestRoutine: boolean;
  /** Ships a settings schema driving generic `RoutineSettingsPanel` rows. */
  hasDeclaredSettings: boolean;
  /**
   * The General tab states what fires the routine instead of opening the trigger
   * editor, leaving only the parameters with a per-user override behind them —
   * a scheduled routine's time of day.
   */
  statesTrigger: boolean;
  /**
   * True when at least one of the above would render something. The Settings
   * tab must be gated on exactly this, or it appears empty.
   */
  hasSettingsTab: boolean;
};

/**
 * The system routines whose General tab states what fires them rather than
 * opening the trigger editor. A list of slugs rather than every system routine:
 * the rest still offer the full form, so narrowing one is a decision taken per
 * routine.
 */
const STATED_TRIGGER_ROUTINES = new Set(["meeting-prep", "daily-briefing"]);

export const getRoutineCapabilities = (routine: RoutineResp): RoutineCapabilities => {
  // Every flag is gated on `kind` first. A system routine's `id` is its slug and
  // a user routine's is an ent id, so the slug comparisons below would in
  // principle be enough — except nothing stops a user routine's ent id from
  // being the string "auto-digest", and the cost of that collision is a
  // dedicated editor rendered over a routine that has none of its data.
  const isSystem = routine.kind === "system";

  const isLabelRoutine = isSystem && routine.id === "auto-inbox";
  const isDigestRoutine = isSystem && routine.id === "auto-digest";
  // Only a shipped routine declares settings — a user routine owns its prompt
  // outright and has nothing to parameterize.
  const hasDeclaredSettings = isSystem && !!routine.settings?.length;

  return {
    isLabelRoutine,
    isDigestRoutine,
    hasDeclaredSettings,
    statesTrigger: isSystem && STATED_TRIGGER_ROUTINES.has(routine.id),
    hasSettingsTab: isDigestRoutine || hasDeclaredSettings,
  };
};
